'use strict';

const libPictView = require('pict-view');
const libDescriptors = require('../EntityDescriptors.js');
const libFocus = require('../FocusFilters.js');
// The shared entity mark: the title block, the labelled cells, and the tooltip body, identical here
// and on every other surface that shows an entity.
const libMarkup = require('./Markup-Templates.js');

// The card's By/For cells (assignee, target) are blank on most cards and cost every card two rows of
// mostly-empty space to hold them. Collapsed by default; "Expanded" reveals them. CSS-only toggle via
// a data attribute on the board root (see PictView-Rail's rail mode), so persisting it and switching it
// never touches the render pipeline.
const _EXPANDED_KEY = 'plansheet.board.expanded';

/**
 * Board — the bespoke Phase-1 Kanban over Work Items, bucketed into the tenant's
 * lanes. Features used to share this stream and now have a board of their own
 * (PictView-FeatureBoard): work items move at agent speed and features move at
 * product speed, and one surface implied they should move together, which is the
 * pressure that produces rubber stamps. HTML5 drag-and-drop
 * moves a card to another lane; the drop PUTs the entity's Status/SortOrder via
 * BoardAPI, which trips the server-side status-change audit. (Deliberately NOT
 * pict-section-flow — that's a node/edge graph editor, reserved for the future
 * retold-workflow definition designer.)
 */
class PictViewBoard extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this._drag = null;
		// The floating drop marker: a single reused node moved between lanes as the
		// cursor travels, so the user sees exactly where the card will land.
		this._placeholder = null;
		// Multi-select state: the set of selected WorkItem numbers, the last-clicked
		// number (for shift-range), and a pending confirm ({ Op, Value, Count, Verb }).
		this._selected = new Set();
		this._lastSelected = null;
		this._confirm = null;
		// IDUser -> roster row, for the focus-view assignee facet (human / AI agent).
		this._rosterIndex = {};
		// How many cards each lane is currently showing, keyed by lane Key (see
		// _decorateLanesForRender): a terminal lane like Done can hold hundreds, so a lane paints its
		// first _MAX_LANE_CARDS and offers a footer that pages in another _MAX_LANE_CARDS at a time.
		// A lane absent from here is at the default cap.
		this._laneShown = {};
	}

	// A large bulk move asks for confirmation (mirroring the MCP preview); a small one
	// applies straight away. Archive always confirms regardless of size.
	get _LARGE_MOVE() { return 10; }

	// How many cards a lane paints before it collapses the rest behind a "show all" footer.
	// A single stream of hundreds of draggable card nodes is slow to drag and forces the lane
	// into its own scrollbars; capping the render keeps the board light and lets the board's own
	// scroll region (not a per-column one) do the scrolling.
	get _MAX_LANE_CARDS() { return 50; }

	_board() { return this.pict.providers.BoardAPI; }

	_router() { return this.pict.providers['Pict-Router']; }

	_boardRoot() { return (typeof document !== 'undefined') ? document.getElementById('ps-board-root') : null; }

	// The persisted intent: whether card By/For cells should show. Defaults off -- most cards leave
	// both blank, so showing them by default was mostly empty space.
	getExpanded()
	{
		try { return localStorage.getItem(_EXPANDED_KEY) === '1'; } catch (pError) { return false; }
	}

	setExpanded(pExpanded)
	{
		let tmpExpanded = !!pExpanded;
		try { localStorage.setItem(_EXPANDED_KEY, tmpExpanded ? '1' : '0'); } catch (pError) { /* best effort */ }
		let tmpRoot = this._boardRoot();
		if (tmpRoot) { tmpRoot.setAttribute('data-board-expanded', tmpExpanded ? '1' : '0'); }
		let tmpButton = (typeof document !== 'undefined') ? document.getElementById('ps-board-expand-btn') : null;
		if (tmpButton)
		{
			tmpButton.textContent = tmpExpanded ? 'Collapse' : 'Expand';
			tmpButton.classList.toggle('is-active', tmpExpanded);
		}
		return tmpExpanded;
	}

	toggleExpanded() { this.setExpanded(!this.getExpanded()); }

	openCard(pKind, pID)
	{
		let tmpRouter = this._router();
		if (!tmpRouter || !pID) return;
		let tmpSegment = (pKind === 'Feature') ? 'feature' : 'work-item';
		tmpRouter.navigate('/' + tmpSegment + '/' + pID);
	}

	refresh()
	{
		let tmpBoard = this._board();
		if (!tmpBoard) { return; }
		// A drag to another lane, a bulk "move to Done", a QC pass/fail -- every status change
		// routes through here, and the render() below replaces #ps-board-root wholesale. On a board
		// wide enough to scroll, that snapped the lanes back to the far left on every single move,
		// which meant working the right side of the board was a fight against the board itself.
		// Capture the scroll now and put it back once the fresh lanes are in the DOM.
		let tmpScroll = this._captureLaneScroll();
		// Re-fetch, then re-apply the active focus-view filter so a Refresh (or a bulk op that
		// calls refresh) keeps the board narrowed instead of snapping back to every card.
		tmpBoard.list().then(() => { this.applyFilter(); this.render(); this._restoreLaneScroll(tmpScroll); });
	}

	// The board's one scroll region (#ps-board-lanes, see the CSS) as it stood right before a
	// re-render tears it down and rebuilds it. Null off-DOM (nothing to preserve).
	_captureLaneScroll()
	{
		let tmpLanes = (typeof document !== 'undefined') ? document.getElementById('ps-board-lanes') : null;
		return tmpLanes ? { Left: tmpLanes.scrollLeft, Top: tmpLanes.scrollTop } : null;
	}

	// Put a captured scroll position back after a re-render. Applied once immediately and once
	// more on the next frame, in case the fresh lanes settle their layout (and therefore their
	// scrollable width) a tick late.
	_restoreLaneScroll(pScroll)
	{
		if (!pScroll) { return; }
		let fApply = () =>
		{
			let tmpLanes = (typeof document !== 'undefined') ? document.getElementById('ps-board-lanes') : null;
			if (tmpLanes) { tmpLanes.scrollLeft = pScroll.Left; tmpLanes.scrollTop = pScroll.Top; }
		};
		fApply();
		if (typeof requestAnimationFrame === 'function') { requestAnimationFrame(fApply); }
	}

	// ── focus-view filtering ─────────────────────────────────────────────────
	// The board narrows the same way the Work Items list does, through the shared engine:
	// a text search, an assignee lens (human / AI), a recent-activity window, plus type and
	// priority. Status is the lanes themselves, so it is not a board facet. The filter lives
	// in the URL (#/board?...) so a narrowed board is shareable and survives reload, AND is
	// remembered per user in localStorage (see _persistState) so it survives navigating away
	// and back -- the router drops the query on every other route, and a plain nav-link back
	// to Board (#/board, no query) used to land on a clean filter with nothing on screen
	// saying it had changed.

	// The last-used board filter, stored the same way EntityList remembers a list's state
	// (see PictView-EntityList._listPrefs): one small blob per user, keyed to this screen.
	_boardPrefs()
	{
		return libFocus.loadListPrefs('Board', this._currentUserID());
	}

	// Remember the live filter state as the board's last-used state. Best-effort, like every
	// other list preference.
	_persistState()
	{
		let tmpPrefs = this._boardPrefs();
		tmpPrefs.state = Object.assign({}, this._filterState());
		libFocus.saveListPrefs('Board', this._currentUserID(), tmpPrefs);
	}

	// The filter state to land on, most specific first: an explicit URL query (a shared /
	// reloaded #/board?... link, or a deep link -- still wins over anything remembered), then
	// the state this person last left the board in, then a clean slate. Called by the app
	// before the board fetch (prepareFilter runs ahead of the list fetch on every board entry).
	prepareFilter()
	{
		if (!this.pict.AppData.Board) { this.pict.AppData.Board = { Lanes: [], Loading: false, Error: null }; }
		if (libFocus.readHashQuery()) { this.pict.AppData.Board.Filter = libFocus.stateFromHash({}); return; }
		let tmpSaved = this._boardPrefs().state;
		this.pict.AppData.Board.Filter = (tmpSaved && typeof tmpSaved === 'object') ? Object.assign(libFocus.emptyState(), tmpSaved) : libFocus.emptyState();
	}

	loadRoster()
	{
		let tmpTeam = this.pict.providers.TeamAPI;
		if (!tmpTeam || typeof tmpTeam.roster !== 'function') { this._rosterIndex = {}; return Promise.resolve([]); }
		return tmpTeam.roster().then((pRoster) =>
		{
			this._rosterIndex = {};
			let tmpList = Array.isArray(pRoster) ? pRoster : [];
			for (let i = 0; i < tmpList.length; i++) { this._rosterIndex[Number(tmpList[i].IDUser)] = tmpList[i]; }
			return tmpList;
		}).catch(() => { this._rosterIndex = {}; return []; });
	}

	_currentUserID()
	{
		let tmpAuth = this.pict.AppData.Auth;
		return (tmpAuth && tmpAuth.User && Number(tmpAuth.User.IDUser)) || 0;
	}

	_filterState()
	{
		if (!this.pict.AppData.Board.Filter) { this.pict.AppData.Board.Filter = libFocus.emptyState(); }
		return this.pict.AppData.Board.Filter;
	}

	// Re-bucket the board from the filtered card set and (re)build the filter-bar model.
	applyFilter()
	{
		let tmpBoard = this._board();
		if (!tmpBoard) { return; }
		let tmpAll = tmpBoard.allCards();
		let tmpContext = libFocus.buildContext((this.pict.AppData.Team && this.pict.AppData.Team.Roster) || [], this._currentUserID());
		let tmpFiltered = libFocus.filterRows(tmpAll, this._filterState(), tmpContext);
		tmpBoard.rebucket(tmpFiltered);
		this.pict.AppData.Board.FilterCount = 'Showing ' + tmpFiltered.length + ' of ' + tmpAll.length + ' cards';
		this._buildFilterBar();
		this._decorateLanesForRender();
	}

	// Per-lane render cap. Each lane keeps its full Cards array (the move/reorder math and the
	// count badge run off that), but the template paints Record.RenderCards -- the first
	// _MAX_LANE_CARDS unless the user expanded the lane -- plus a MoreSlot footer that toggles
	// the lane open. RenderCards is always the FIRST N of Cards, so a rendered card's DOM index
	// still equals its model index and the drop-position math stays correct.
	_decorateLanesForRender()
	{
		let tmpLanes = (this.pict.AppData.Board && this.pict.AppData.Board.Lanes) || [];
		let tmpMax = this._MAX_LANE_CARDS;
		for (let i = 0; i < tmpLanes.length; i++)
		{
			let tmpLane = tmpLanes[i];
			let tmpCards = tmpLane.Cards || [];
			// How many this lane is currently showing. Grows a page at a time as the person asks for
			// more, rather than flipping straight to all of them: a Done lane can hold hundreds, and
			// painting them all at once is exactly the cost the cap exists to avoid.
			let tmpShown = Math.min(this._laneShown[tmpLane.Key] || tmpMax, tmpCards.length);
			tmpLane.RenderCards = (tmpCards.length <= tmpShown) ? tmpCards : tmpCards.slice(0, tmpShown);

			let tmpHidden = tmpCards.length - tmpLane.RenderCards.length;
			if (tmpHidden > 0)
			{
				tmpLane.MoreSlot = [{
					Key: tmpLane.Key, Action: 'more',
					Label: '... ' + tmpHidden + ' more card' + ((tmpHidden === 1) ? '' : 's'),
					Button: 'Load ' + Math.min(tmpMax, tmpHidden) + ' more'
				}];
			}
			else if (tmpShown > tmpMax)
			{
				// Fully shown and expanded past the cap: offer the way back.
				tmpLane.MoreSlot = [{
					Key: tmpLane.Key, Action: 'fewer',
					Label: 'Showing all ' + tmpCards.length, Button: 'Show fewer'
				}];
			}
			else { tmpLane.MoreSlot = []; }
		}
	}

	// Footer action: show one more page of a lane, or collapse it back to the cap. Paging rather than
	// an all-or-nothing toggle, so a lane with hundreds of cards never has to paint them all to show
	// the next few.
	laneMore(pKey, pAction)
	{
		if (!pKey) { return; }
		let tmpMax = this._MAX_LANE_CARDS;
		if (pAction === 'fewer') { delete this._laneShown[pKey]; }
		else { this._laneShown[pKey] = (this._laneShown[pKey] || tmpMax) + tmpMax; }
		this._repaintLanes();
	}

	_opt(pValues, pSelected, pAnyLabel)
	{
		return pValues.map((pV) => ({ Value: pV, Label: (pV === '') ? pAnyLabel : pV, SelectedAttr: (String(pV) === String(pSelected || '')) ? 'selected' : '' }));
	}

	_assigneeOptions(pSelected)
	{
		let tmpOptions = libFocus.AssigneeChoices.map((pC) => ({ Value: pC.Key, Label: pC.Label, SelectedAttr: (pC.Key === (pSelected || '')) ? 'selected' : '' }));
		let tmpRoster = (this.pict.AppData.Team && this.pict.AppData.Team.Roster) || [];
		for (let i = 0; i < tmpRoster.length; i++)
		{
			let tmpKey = 'u:' + tmpRoster[i].IDUser;
			tmpOptions.push({ Value: tmpKey, Label: (tmpRoster[i].DisplayName || ('#' + tmpRoster[i].IDUser)) + (Number(tmpRoster[i].IsAgent) === 1 ? ' (AI)' : ''), SelectedAttr: (tmpKey === (pSelected || '')) ? 'selected' : '' });
		}
		return tmpOptions;
	}

	_recentOptions(pSelected)
	{
		let tmpOptions = [{ Value: '', Label: 'Any time', SelectedAttr: (!pSelected) ? 'selected' : '' }];
		libFocus.RecentWindows.forEach((pW) => tmpOptions.push({ Value: pW.Key, Label: pW.Label, SelectedAttr: (pW.Key === (pSelected || '')) ? 'selected' : '' }));
		return tmpOptions;
	}

	_viewOptions(pSelected)
	{
		let tmpOptions = [{ Value: '', Label: 'Views: none', SelectedAttr: (!pSelected) ? 'selected' : '' }];
		// Board and list share one saved-view set (keyed 'WorkItem'), so a saved focus works on both.
		libFocus.allViews('WorkItem', this._currentUserID()).forEach((pV) => tmpOptions.push({ Value: pV.Key, Label: (pV.Group ? (pV.Group + ': ') : '') + pV.Label, SelectedAttr: (pV.Key === (pSelected || '')) ? 'selected' : '' }));
		return tmpOptions;
	}

	_buildFilterBar()
	{
		let tmpState = this._filterState();
		this.pict.AppData.Board.FilterBar =
		{
			SearchValue: String(tmpState.q || ''),
			ViewOptions: this._viewOptions(tmpState.view),
			TypeOptions: this._opt([''].concat(libDescriptors.WorkItemTypes), tmpState.type, 'All types'),
			PriorityOptions: this._opt([''].concat(libDescriptors.Priorities), tmpState.priority, 'Any priority'),
			AssigneeOptions: this._assigneeOptions(tmpState.assignee),
			RecentOptions: this._recentOptions(tmpState.recent),
			WorkflowStatusOptions: libFocus.WorkflowStatusChoices.map((pC) => ({ Value: pC.Key, Label: pC.Label, SelectedAttr: (pC.Key === (tmpState.wfstatus || '')) ? 'selected' : '' })),
			CountLabel: this.pict.AppData.Board.FilterCount || '',
			ClearSlot: libFocus.isFiltered(tmpState) ? [{}] : [],
			DeleteViewSlot: (String(tmpState.view || '').indexOf('saved:') === 0) ? [{ Key: tmpState.view }] : []
		};
		this.pict.AppData.Board.FilterBarSlot = [this.pict.AppData.Board.FilterBar];
	}

	setFilter(pField, pValue)
	{
		this._filterState()[pField] = pValue || '';
		this._filterState().view = '';
		this._afterFilterChange();
	}

	// The search box: re-bucket + repaint only the lanes (keep the input focused mid-type).
	setSearch(pValue)
	{
		this._filterState().q = pValue || '';
		this._filterState().view = '';
		this._writeUrl();
		this._persistState();
		this.applyFilter();
		this._repaintLanes();
	}

	applyView(pKey)
	{
		if (!pKey) { this.clearFilters(); return; }
		let tmpState = libFocus.viewState('WorkItem', this._currentUserID(), pKey);
		this.pict.AppData.Board.Filter = tmpState || libFocus.emptyState();
		this._afterFilterChange();
	}

	clearFilters()
	{
		this.pict.AppData.Board.Filter = libFocus.emptyState();
		this._afterFilterChange();
	}

	// Shared post-change tail for every facet mutation: rewrite the URL, remember the state as
	// this person's last-used board filter, re-bucket, and re-render. Persisting here is what
	// makes a narrowed board survive walking away from it -- and what makes Clear honest, since
	// clearing used to be undone the moment you navigated back with nothing on screen saying so.
	_afterFilterChange()
	{
		this._writeUrl();
		this._persistState();
		this.applyFilter();
		this.render();
	}

	saveView()
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (!tmpModal || typeof tmpModal.show !== 'function') { return; }
		let fSave = (pName) =>
		{
			let tmpRecord = libFocus.saveSavedView('WorkItem', this._currentUserID(), pName, this._filterState());
			if (tmpRecord) { this._filterState().view = tmpRecord.Key; this._afterFilterChange(); this._toast('View "' + tmpRecord.Label + '" saved.', 'success'); }
		};
		tmpModal.show(
			{
				title: 'Save this view',
				content: '<p>Name this filter so you can switch back to it:</p><input type="text" id="ps-boardview-input" class="ps-input" style="width:100%" autofocus>',
				buttons: [ { Hash: 'cancel', Label: 'Cancel' }, { Hash: 'ok', Label: 'Save', Style: 'primary' } ]
			}).then((pChoice) =>
			{
				if (pChoice !== 'ok') { return; }
				let tmpEl = (typeof document !== 'undefined') ? document.getElementById('ps-boardview-input') : null;
				let tmpName = (tmpEl && tmpEl.value ? tmpEl.value : '').trim();
				if (tmpName) { fSave(tmpName); }
			});
	}

	// QC review: pass a Review-lane Work Item straight to Done. The event's stopPropagation keeps
	// the card's onclick (open detail) from firing.
	reviewPass(pEvent, pID)
	{
		if (pEvent && typeof pEvent.stopPropagation === 'function') { pEvent.stopPropagation(); }
		let tmpBoard = this._board();
		if (!tmpBoard) { return; }
		tmpBoard.reviewWorkItem(pID, 'Pass', {})
			.then(() => { this.refresh(); this._toast('Passed review -> Done.', 'success'); })
			.catch((pErr) => { this._toast('Could not pass review: ' + (pErr.message || pErr), 'error'); });
	}

	// QC review: "did not pass QC". Collect a reason code, a note, and an optional punch-list of
	// issue titles, then send the card back for rework.
	reviewFail(pEvent, pID)
	{
		if (pEvent && typeof pEvent.stopPropagation === 'function') { pEvent.stopPropagation(); }
		let tmpBoard = this._board();
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (!tmpBoard || !tmpModal || typeof tmpModal.show !== 'function') { return; }

		let fEsc = (pText) => String(pText == null ? '' : pText).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
		let fField = (pLabel, pControl) => '<label style="display:block;margin:8px 0 4px;font-size:12px;font-weight:600;color:var(--theme-color-text-secondary,#5b6376)">' + fEsc(pLabel) + '</label>' + pControl;
		let tmpStyle = 'width:100%;box-sizing:border-box;padding:6px 8px;';
		let tmpReasonOptions = (libDescriptors.QCReasonCodes || []).map((pCode) => '<option value="' + fEsc(pCode) + '">' + fEsc(pCode) + '</option>').join('');

		tmpModal.show(
			{
				title: 'Send back (did not pass QC)',
				content:
					fField('Reason', '<select id="qc-reason" style="' + tmpStyle + '">' + tmpReasonOptions + '</select>') +
					fField('What was wrong / what is needed', '<textarea id="qc-note" style="' + tmpStyle + 'min-height:64px;resize:vertical;" placeholder="A short note for the person picking this back up..."></textarea>') +
					fField('Punch-list issues (optional, one per line)', '<textarea id="qc-issues" style="' + tmpStyle + 'min-height:52px;resize:vertical;" placeholder="Each line becomes an Issue linked to this work item"></textarea>'),
				buttons: [ { Hash: 'cancel', Label: 'Cancel' }, { Hash: 'ok', Label: 'Send back', Style: 'primary' } ]
			}).then((pChoice) =>
			{
				if (pChoice !== 'ok') { return; }
				let fVal = (pElID) => { let tmpEl = (typeof document !== 'undefined') ? document.getElementById(pElID) : null; return tmpEl ? tmpEl.value : ''; };
				let tmpIssues = fVal('qc-issues').split('\n').map((pLine) => pLine.trim()).filter((pLine) => pLine);
				tmpBoard.reviewWorkItem(pID, 'Fail', { ReasonCode: fVal('qc-reason'), Note: fVal('qc-note').trim(), Issues: tmpIssues })
					.then((pResult) =>
					{
						this.refresh();
						let tmpN = (pResult && pResult.Issues && pResult.Issues.length) || 0;
						this._toast('Sent back for rework' + (tmpN ? (' with ' + tmpN + ' issue' + (tmpN === 1 ? '' : 's')) : '') + '.', 'success');
					})
					.catch((pErr) => { this._toast('Could not send back: ' + (pErr.message || pErr), 'error'); });
			});
	}

	deleteView(pKey)
	{
		let tmpKey = pKey || this._filterState().view;
		if (!tmpKey) { return; }
		libFocus.deleteSavedView('WorkItem', this._currentUserID(), tmpKey);
		if (this._filterState().view === tmpKey) { this._filterState().view = ''; }
		this._afterFilterChange();
		this._toast('View deleted.', 'success');
	}

	_writeUrl() { libFocus.writeHashState(this._filterState()); }

	// Re-render only the lanes container + count (used by the search box so focus stays put).
	_repaintLanes()
	{
		this._decorateLanesForRender();
		let tmpLanes = this.pict.AppData.Board.Lanes || [];
		let tmpHTML = '';
		for (let i = 0; i < tmpLanes.length; i++) { tmpHTML += this.pict.parseTemplateByHash('Board-Lane', tmpLanes[i]); }
		this.pict.ContentAssignment.assignContent('#ps-board-lanes', tmpHTML);
		this.pict.ContentAssignment.assignContent('#ps-board-count', this.pict.AppData.Board.FilterCount || '');
		this._applySelectionClasses();
		// Every card element above is a new one, so the tooltips have to be rebound the way a full
		// render rebinds them (onAfterRender). Without this, peeking silently stopped working after
		// the first keystroke in the search box, and the old handles were left holding detached cards.
		this._attachCardTooltips();
	}

	dragStart(pEvent, pKind, pID)
	{
		// Peeking must never fight dragging: tear the tooltips down for the duration of the gesture and
		// put them back when it ends. A drop relocates the card element rather than re-rendering, so
		// dragEnd has to restore them itself.
		this._destroyCardTooltips();
		// Stash the dragged card element so drop() can physically relocate it
		// instead of re-rendering the whole board.
		let tmpElement = pEvent && (pEvent.currentTarget || (pEvent.target && pEvent.target.closest && pEvent.target.closest('.ps-card')));
		// Group-drag: if the grabbed card is one of several selected work items, drag the
		// whole selection to the drop lane instead of just this card.
		let tmpNumber = tmpElement && tmpElement.getAttribute ? (Number(tmpElement.getAttribute('data-number')) || 0) : 0;
		if (pKind === 'WorkItem' && this._selected.size > 1 && this._selected.has(tmpNumber))
		{
			this._drag = { Group: true, Numbers: Array.from(this._selected), Element: tmpElement || null };
		}
		else
		{
			this._drag = { Kind: pKind, ID: pID, Element: tmpElement || null };
		}
		if (tmpElement && tmpElement.classList) tmpElement.classList.add('ps-card-dragging');
		if (pEvent && pEvent.dataTransfer)
		{
			pEvent.dataTransfer.effectAllowed = 'move';
			try { pEvent.dataTransfer.setData('text/plain', String(pID)); } catch (pErr) { /* ignore */ }
		}
	}

	dragEnd(pEvent)
	{
		let tmpElement = pEvent && pEvent.currentTarget;
		if (tmpElement && tmpElement.classList) tmpElement.classList.remove('ps-card-dragging');
		// The gesture is over, so peeking is welcome again.
		this._attachCardTooltips();
		// The drag ended (dropped anywhere, or cancelled) — tidy the marker and any
		// lingering hover state so nothing is left dangling.
		this._removePlaceholder();
		this._clearDragoverLanes();
		this._drag = null;
	}

	// The reused drop-marker node, created lazily the first time it is needed.
	_placeholderNode()
	{
		if (typeof document === 'undefined') return null;
		if (!this._placeholder)
		{
			this._placeholder = document.createElement('div');
			this._placeholder.className = 'ps-card-placeholder';
		}
		return this._placeholder;
	}

	_removePlaceholder()
	{
		if (this._placeholder && this._placeholder.parentNode)
		{
			this._placeholder.parentNode.removeChild(this._placeholder);
		}
	}

	_clearDragoverLanes()
	{
		if (typeof document === 'undefined') return;
		let tmpLanes = document.querySelectorAll('.ps-lane.ps-lane-dragover');
		for (let i = 0; i < tmpLanes.length; i++) tmpLanes[i].classList.remove('ps-lane-dragover');
	}

	/**
	 * The card element the marker should sit ABOVE for a given cursor Y — the first
	 * card whose vertical midpoint is below the cursor. The card being dragged is
	 * skipped so it never counts as its own neighbor. Null means "past the last card".
	 */
	_cardBefore(pCardsElement, pY)
	{
		let tmpCards = pCardsElement.querySelectorAll('.ps-card');
		for (let i = 0; i < tmpCards.length; i++)
		{
			if (this._drag && tmpCards[i] === this._drag.Element) continue;
			let tmpRect = tmpCards[i].getBoundingClientRect();
			if (pY < tmpRect.top + (tmpRect.height / 2)) return tmpCards[i];
		}
		return null;
	}

	allowDrop(pEvent)
	{
		if (pEvent && typeof pEvent.preventDefault === 'function') pEvent.preventDefault();
		if (pEvent && pEvent.dataTransfer) pEvent.dataTransfer.dropEffect = 'move';
		let tmpLane = pEvent && pEvent.currentTarget;
		if (!tmpLane) return;
		if (tmpLane.classList) tmpLane.classList.add('ps-lane-dragover');
		if (!this._drag) return;

		// Slot the marker into this lane at the cursor position. Moving the single
		// node here also pulls it out of whatever lane it was in before.
		let tmpCardsElement = tmpLane.querySelector ? tmpLane.querySelector('.ps-lane-cards') : null;
		let tmpPlaceholder = this._placeholderNode();
		if (!tmpCardsElement || !tmpPlaceholder) return;
		let tmpY = (pEvent && typeof pEvent.clientY === 'number') ? pEvent.clientY : 0;
		let tmpBefore = this._cardBefore(tmpCardsElement, tmpY);
		if (tmpBefore) tmpCardsElement.insertBefore(tmpPlaceholder, tmpBefore);
		else tmpCardsElement.appendChild(tmpPlaceholder);
	}

	dragLeave(pEvent)
	{
		let tmpLane = pEvent && pEvent.currentTarget;
		if (!tmpLane) return;
		// dragleave also fires when crossing between the lane's own children; only
		// treat it as a real exit when the cursor moved to something outside the lane.
		let tmpRelated = pEvent.relatedTarget;
		if (tmpRelated && tmpLane.contains && tmpLane.contains(tmpRelated)) return;
		if (tmpLane.classList) tmpLane.classList.remove('ps-lane-dragover');
		if (this._placeholder && tmpLane.contains && tmpLane.contains(this._placeholder))
		{
			this._removePlaceholder();
		}
	}

	drop(pEvent, pLaneKey)
	{
		if (pEvent && typeof pEvent.preventDefault === 'function') pEvent.preventDefault();
		let tmpLaneElement = pEvent && pEvent.currentTarget;
		if (tmpLaneElement && tmpLaneElement.classList) tmpLaneElement.classList.remove('ps-lane-dragover');

		let tmpDrag = this._drag;
		this._drag = null;
		let tmpBoard = this._board();
		if (tmpDrag && tmpDrag.Element && tmpDrag.Element.classList) tmpDrag.Element.classList.remove('ps-card-dragging');

		// Group-drag: move the whole selection to this lane through the bulk endpoint,
		// then re-fetch. (A single optimistic relocate does not model N cards moving.)
		if (tmpDrag && tmpDrag.Group)
		{
			this._removePlaceholder();
			this._clearDragoverLanes();
			this.moveSelection(pLaneKey);
			return;
		}

		let tmpCardsElement = tmpLaneElement ? tmpLaneElement.querySelector('.ps-lane-cards') : null;
		let tmpPlaceholder = this._placeholder;

		// The marker's spot is the drop target: count the real cards above it (the
		// dragged card excluded) to get the resting index among the lane's others.
		let tmpTargetIndex = this._markerIndex(tmpCardsElement, tmpPlaceholder, tmpDrag);

		if (!tmpDrag || !tmpBoard) { this._removePlaceholder(); return; }

		// Optimistic move: update the in-memory model and relocate just the card node
		// to the marker's slot. No full re-render, so the board keeps its horizontal
		// scroll and each lane keeps its vertical scroll.
		let tmpMove = tmpBoard.applyLocalMoveAt(tmpDrag.Kind, tmpDrag.ID, pLaneKey, tmpTargetIndex);
		if (!tmpMove) { this._removePlaceholder(); return; }

		if (tmpDrag.Element && tmpCardsElement)
		{
			if (tmpPlaceholder && tmpPlaceholder.parentNode === tmpCardsElement)
			{
				tmpCardsElement.insertBefore(tmpDrag.Element, tmpPlaceholder);
			}
			else
			{
				tmpCardsElement.appendChild(tmpDrag.Element);
			}
		}
		this._removePlaceholder();
		this._syncLaneCounts(tmpLaneElement);

		tmpBoard.persistMove(tmpMove)
			.catch((pErr) =>
			{
				// Roll the model back and fall back to a full re-render on the rare
				// failure path so the board reflects the real server state.
				let tmpScroll = this._captureLaneScroll();
				tmpBoard.revertLocalMove(tmpMove);
				this.render();
				this._restoreLaneScroll(tmpScroll);
				let tmpModal = this.pict.views['Pict-Section-Modal'];
				if (tmpModal && typeof tmpModal.toast === 'function')
				{
					tmpModal.toast('Move failed: ' + (pErr.message || pErr), { type: 'error', duration: 6000 });
				}
			});
	}

	/**
	 * Resting index for the dropped card = number of real cards (the dragged card
	 * excluded) ahead of the marker. Falls back to the end of the lane if the marker
	 * is not placed (e.g. a drop with no preceding dragover).
	 */
	_markerIndex(pCardsElement, pPlaceholder, pDrag)
	{
		if (!pCardsElement) return Infinity;
		if (!pPlaceholder || pPlaceholder.parentNode !== pCardsElement) return Infinity;
		let tmpNodes = pCardsElement.querySelectorAll('.ps-card, .ps-card-placeholder');
		let tmpIndex = 0;
		for (let i = 0; i < tmpNodes.length; i++)
		{
			if (tmpNodes[i] === pPlaceholder) break;
			if (pDrag && tmpNodes[i] === pDrag.Element) continue;
			tmpIndex++;
		}
		return tmpIndex;
	}

	/**
	 * Refresh every lane's count badge in place from the model, without a render.
	 * pAnyLaneElement is any lane node used to locate the shared lanes container.
	 */
	_syncLaneCounts(pAnyLaneElement)
	{
		let tmpBoard = this._board();
		if (!tmpBoard) return;
		let tmpLanes = (tmpBoard.store() && tmpBoard.store().Lanes) || [];
		let tmpRoot = pAnyLaneElement && typeof pAnyLaneElement.closest === 'function' ? pAnyLaneElement.closest('.ps-board-lanes') : null;
		if (!tmpRoot) return;
		let tmpCounts = tmpRoot.querySelectorAll('.ps-lane .ps-lane-count');
		for (let i = 0; i < tmpCounts.length && i < tmpLanes.length; i++)
		{
			tmpCounts[i].textContent = tmpLanes[i].Count;
		}
	}

	// ---- Multi-select ---------------------------------------------------------------

	_esc(pText)
	{
		return String(pText == null ? '' : pText).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
	}

	// Work-item numbers in one lane, in DOM (visual) order -- the span a shift-click walks.
	_laneNumbers(pLaneElement)
	{
		let tmpOut = [];
		if (!pLaneElement || !pLaneElement.querySelectorAll) return tmpOut;
		let tmpCards = pLaneElement.querySelectorAll('.ps-card-workitem');
		for (let i = 0; i < tmpCards.length; i++)
		{
			let tmpNumber = Number(tmpCards[i].getAttribute('data-number')) || 0;
			if (tmpNumber > 0) tmpOut.push(tmpNumber);
		}
		return tmpOut;
	}

	// Toggle one card's selection. Shift-click extends the range from the last click to
	// here within the same lane (the usual list-select gesture). Called from the card's
	// checkbox, so it stops the click from opening the card.
	toggleSelect(pEvent, pNumber)
	{
		if (pEvent && typeof pEvent.stopPropagation === 'function') pEvent.stopPropagation();
		pNumber = Number(pNumber) || 0;
		if (!pNumber) return;

		let tmpCheckbox = pEvent && pEvent.target;
		let tmpLane = tmpCheckbox && tmpCheckbox.closest ? tmpCheckbox.closest('.ps-lane') : null;

		if (pEvent && pEvent.shiftKey && this._lastSelected && tmpLane)
		{
			let tmpNumbers = this._laneNumbers(tmpLane);
			let tmpFrom = tmpNumbers.indexOf(this._lastSelected);
			let tmpTo = tmpNumbers.indexOf(pNumber);
			if (tmpFrom >= 0 && tmpTo >= 0)
			{
				let tmpLow = Math.min(tmpFrom, tmpTo);
				let tmpHigh = Math.max(tmpFrom, tmpTo);
				for (let i = tmpLow; i <= tmpHigh; i++) this._selected.add(tmpNumbers[i]);
			}
			else { this._selected.add(pNumber); }
		}
		else if (this._selected.has(pNumber)) { this._selected.delete(pNumber); }
		else { this._selected.add(pNumber); }

		this._lastSelected = pNumber;
		this._confirm = null;
		this._applySelectionClasses();
		this._syncBulkBar();
	}

	clearSelection()
	{
		this._selected.clear();
		this._lastSelected = null;
		this._confirm = null;
		this._applySelectionClasses();
		this._syncBulkBar();
	}

	// Reflect the selection set onto the rendered cards (checkbox + highlight), pruning
	// any numbers whose card is no longer on the board.
	_applySelectionClasses()
	{
		if (typeof document === 'undefined') return;
		let tmpCards = document.querySelectorAll('.ps-card-workitem');
		let tmpPresent = {};
		for (let i = 0; i < tmpCards.length; i++)
		{
			let tmpNumber = Number(tmpCards[i].getAttribute('data-number')) || 0;
			tmpPresent[tmpNumber] = true;
			let tmpSelected = this._selected.has(tmpNumber);
			if (tmpCards[i].classList) tmpCards[i].classList.toggle('ps-card-selected', tmpSelected);
			let tmpCheckbox = tmpCards[i].querySelector('.ps-card-check');
			if (tmpCheckbox) tmpCheckbox.checked = tmpSelected;
		}
		// Drop selections whose card left the board (archived / filtered), so the count is honest.
		let tmpStale = [];
		this._selected.forEach((pNumber) => { if (!tmpPresent[pNumber]) tmpStale.push(pNumber); });
		tmpStale.forEach((pNumber) => this._selected.delete(pNumber));
	}

	// ---- Bulk action bar ------------------------------------------------------------

	_lanes()
	{
		let tmpBoard = this._board();
		return (tmpBoard && tmpBoard.store() && tmpBoard.store().Lanes) || [];
	}

	// Build (or update) the floating bar. Hidden when nothing is selected and no confirm
	// is pending. Rebuilt on every render, so it survives a board re-render.
	_syncBulkBar()
	{
		if (typeof document === 'undefined') return;
		let tmpRoot = document.querySelector('.ps-board');
		if (!tmpRoot) return;

		let tmpBar = tmpRoot.querySelector('#ps-bulk-bar');
		let tmpCount = this._selected.size;
		if (tmpCount === 0 && !this._confirm)
		{
			if (tmpBar && tmpBar.parentNode) tmpBar.parentNode.removeChild(tmpBar);
			return;
		}
		if (!tmpBar)
		{
			tmpBar = document.createElement('div');
			tmpBar.id = 'ps-bulk-bar';
			tmpBar.className = 'ps-bulk-bar';
			tmpRoot.appendChild(tmpBar);
		}

		let tmpView = "_Pict.views['Plansheet-Board']";
		if (this._confirm)
		{
			// Confirm step mirrors the preview: it names the action and the card count.
			tmpBar.innerHTML =
				'<span class="ps-bulk-count">' + this._esc(this._confirm.Verb) + ' ' + this._confirm.Count + ' card' + (this._confirm.Count === 1 ? '' : 's') + '?</span>' +
				'<span class="ps-bulk-note">' + this._esc(this._confirm.Note || '') + '</span>' +
				'<span class="ps-bulk-spacer"></span>' +
				'<button class="ps-btn ps-bulk-confirm" onclick="' + tmpView + '.confirmBulk()">Confirm</button>' +
				'<button class="ps-btn" onclick="' + tmpView + '.cancelConfirm()">Cancel</button>';
			return;
		}

		let tmpLaneOptions = '<option value="">Move to lane...</option>';
		let tmpLanes = this._lanes();
		for (let i = 0; i < tmpLanes.length; i++)
		{
			tmpLaneOptions += '<option value="' + this._esc(tmpLanes[i].Key) + '">' + this._esc(tmpLanes[i].Label || tmpLanes[i].Key) + '</option>';
		}
		let tmpPriorityOptions = '<option value="">Priority...</option>' + ['Low', 'Normal', 'High'].map((pP) => '<option value="' + pP + '">' + pP + '</option>').join('');
		let tmpTypeOptions = '<option value="">Type...</option>' + ['Triage', 'Research', 'Innovation', 'Issue', 'Debt', 'Enhancement'].map((pT) => '<option value="' + pT + '">' + pT + '</option>').join('');
		// Assignee options from the loaded roster (humans + AI agents); Value is the raw IDUser, 0 clears it.
		let tmpBulkRoster = (this.pict.AppData.Team && this.pict.AppData.Team.Roster) || [];
		let tmpAssigneeOptions = '<option value="">Assign to...</option><option value="0">Unassigned</option>';
		for (let i = 0; i < tmpBulkRoster.length; i++)
		{
			tmpAssigneeOptions += '<option value="' + this._esc(String(tmpBulkRoster[i].IDUser)) + '">' + this._esc((tmpBulkRoster[i].DisplayName || ('#' + tmpBulkRoster[i].IDUser)) + (Number(tmpBulkRoster[i].IsAgent) === 1 ? ' (AI)' : '')) + '</option>';
		}

		tmpBar.innerHTML =
			'<span class="ps-bulk-count">' + tmpCount + ' selected</span>' +
			'<select class="ps-bulk-select" onchange="' + tmpView + '.moveSelection(this.value); this.selectedIndex=0;">' + tmpLaneOptions + '</select>' +
			'<select class="ps-bulk-select" onchange="' + tmpView + '.setSelectionPriority(this.value); this.selectedIndex=0;">' + tmpPriorityOptions + '</select>' +
			'<select class="ps-bulk-select" onchange="' + tmpView + '.setSelectionType(this.value); this.selectedIndex=0;">' + tmpTypeOptions + '</select>' +
			'<select class="ps-bulk-select" onchange="' + tmpView + '.setSelectionAssignee(this.value); this.selectedIndex=0;">' + tmpAssigneeOptions + '</select>' +
			'<button class="ps-btn ps-bulk-archive" onclick="' + tmpView + '.beginArchive()">Archive</button>' +
			'<span class="ps-bulk-spacer"></span>' +
			'<button class="ps-btn" onclick="' + tmpView + '.clearSelection()">Clear</button>';
	}

	_selector()
	{
		return { IDs: Array.from(this._selected) };
	}

	_toast(pMessage, pType)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && typeof tmpModal.toast === 'function')
		{
			tmpModal.toast(pMessage, { type: pType || 'info', duration: 5000 });
		}
	}

	// Apply a bulk operation (Confirm:true), then report and re-fetch the board.
	//
	// Deliberately does NOT clear this._selected: a bulk action (Assign, Move, SetPriority, ...) is
	// rarely the only thing a checked-off set of cards needs -- "Assign to Claude" then "Move to Todo"
	// is the common chain, and losing the selection meant reselecting the same cards for every step.
	// refresh() -> render() -> _applySelectionClasses() already prunes any number whose card left the
	// board (archived, or filtered out by the active view), so an Archive still empties the bar on its
	// own; anything left on the board just stays checked for the next action.
	_applyBulk(pOperation, pValue)
	{
		let tmpBoard = this._board();
		if (!tmpBoard || this._selected.size === 0) return;
		let tmpSelector = this._selector();
		this._confirm = null;
		tmpBoard.bulkOperation(pOperation, tmpSelector, pValue, true)
			.then((pResult) =>
			{
				let tmpApplied = (pResult && pResult.Applied) || 0;
				let tmpFailed = (pResult && pResult.Failed && pResult.Failed.length) || 0;
				this.refresh();
				this._toast(tmpApplied + ' updated' + (tmpFailed ? (', ' + tmpFailed + ' failed') : ''), tmpFailed ? 'error' : 'success');
			})
			.catch((pErr) =>
			{
				this._toast('Bulk change failed: ' + (pErr.message || pErr), 'error');
			});
	}

	// Preview first, then show the confirm step (used by Archive and large moves).
	_previewThenConfirm(pOperation, pValue, pVerb)
	{
		let tmpBoard = this._board();
		if (!tmpBoard || this._selected.size === 0) return;
		tmpBoard.bulkOperation(pOperation, this._selector(), pValue, false)
			.then((pPreview) =>
			{
				this._confirm =
				{
					Op: pOperation,
					Value: pValue,
					Verb: pVerb,
					Count: (pPreview && pPreview.Count) || this._selected.size,
					Note: (pOperation === 'Archive') ? 'Removes them from the board (recoverable).' : ''
				};
				this._syncBulkBar();
			})
			.catch((pErr) => this._toast('Could not preview: ' + (pErr.message || pErr), 'error'));
	}

	confirmBulk()
	{
		if (!this._confirm) return;
		let tmpOp = this._confirm.Op;
		let tmpValue = this._confirm.Value;
		this._applyBulk(tmpOp, tmpValue);
	}

	cancelConfirm()
	{
		this._confirm = null;
		this._syncBulkBar();
	}

	// Move the selection to a lane. A large move confirms first; a small one applies.
	moveSelection(pLaneKey)
	{
		if (!pLaneKey || this._selected.size === 0) return;
		if (this._selected.size >= this._LARGE_MOVE)
		{
			let tmpLane = this._lanes().find((pL) => pL.Key === pLaneKey);
			this._previewThenConfirm('Move', pLaneKey, 'Move to ' + ((tmpLane && tmpLane.Label) || pLaneKey) + ':');
			return;
		}
		this._applyBulk('Move', pLaneKey);
	}

	setSelectionPriority(pPriority)
	{
		if (!pPriority || this._selected.size === 0) return;
		this._applyBulk('SetPriority', pPriority);
	}

	setSelectionType(pType)
	{
		if (!pType || this._selected.size === 0) return;
		this._applyBulk('SetType', pType);
	}

	// Assign the selection to a user (raw IDUser), or 0 to clear. Applies directly, like priority/type.
	setSelectionAssignee(pIDUser)
	{
		if (pIDUser === '' || pIDUser == null || this._selected.size === 0) return;
		this._applyBulk('Assign', pIDUser);
	}

	// Archive always confirms (mirroring the MCP preview requirement).
	beginArchive()
	{
		if (this._selected.size === 0) return;
		this._previewThenConfirm('Archive', null, 'Archive');
	}

	/**
	 * The loaded tooltip. Hover to peek at a card without leaving the board, click to pin it open and
	 * read the cross-references -- which is the whole point of the exercise: managing the board without
	 * bouncing in and out of detail views.
	 *
	 * Three things this has to get right. It must never appear mid-drag, or peeking fights dragging on
	 * the one gesture the board exists for. Every handle must be destroyed on re-render, because the
	 * board re-renders on every filter and lane move, and leaked handles would stack up invisibly. And
	 * the same on the way out of the board entirely, which is what teardown() below is for.
	 */
	_attachCardTooltips()
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (!tmpModal || typeof tmpModal.pinnableTooltip !== 'function') { return; }

		this._destroyCardTooltips();
		this._tooltipHandles = [];
		let tmpCards = this.pict.AppData.Board && this.pict.AppData.Board.AllCards;
		if (!Array.isArray(tmpCards)) { return; }
		let tmpByKey = {};
		tmpCards.forEach((pCard) => { tmpByKey[pCard.CardKind + ':' + pCard.ID] = pCard; });

		// Scoped to the board's own lanes, not the document: a stray .ps-card left in the DOM by
		// another surface can never be adopted, and an attach attempted when the board is not on
		// screen binds nothing at all rather than anchoring tooltips to elements we do not own.
		let tmpElements = document.querySelectorAll('#ps-board-lanes .ps-card');
		for (let i = 0; i < tmpElements.length; i++)
		{
			let tmpElement = tmpElements[i];
			let tmpCard = tmpByKey[(tmpElement.classList.contains('ps-card-feature') ? 'Feature' : 'WorkItem')
				+ ':' + tmpElement.getAttribute('data-id')];
			if (!tmpCard) { continue; }
			this._tooltipHandles.push(tmpModal.pinnableTooltip(tmpElement, this._tooltipHTML(tmpCard),
				{ position: 'right', delay: 320, maxWidth: 400, interactive: true }));
		}
	}

	_destroyCardTooltips()
	{
		(this._tooltipHandles || []).forEach((pHandle) =>
		{
			try { if (pHandle && typeof pHandle.destroy === 'function') { pHandle.destroy(); } }
			catch (pError) { /* the element is already gone; nothing to tidy */ }
		});
		this._tooltipHandles = [];
	}

	/**
	 * Leaving the board has to take the tooltips with it, and only the board's own render path was
	 * ever destroying them. A tooltip is anchored to a card element, so the moment another view
	 * paints over the workspace that anchor is detached -- and a detached element never fires
	 * mouseleave, so a tooltip open on the way out hangs on screen over the next page (a work-item
	 * peek floating above the Work Items list, naming a row that is not even there). The handles
	 * leak with it, re-created on every board visit.
	 *
	 * The application calls this when it routes away. Idempotent: a board that was never rendered,
	 * or one torn down twice, is a no-op.
	 *
	 * Also drops the floating bulk-action bar (#ps-bulk-bar) if one is still up. It is a raw node
	 * appended to .ps-board outside the template render cycle (see _syncBulkBar), and since a bulk
	 * action now deliberately leaves the selection in place for chaining (WI #359), a selection can
	 * still be non-empty when the user routes away mid-chain -- if the router's own re-render does not
	 * happen to remove it, a fixed-position bar left behind would go on intercepting clicks on
	 * whatever page comes next, the same way an untorn-down tooltip used to.
	 */
	teardown()
	{
		this._destroyCardTooltips();
		if (typeof document !== 'undefined')
		{
			let tmpBar = document.getElementById('ps-bulk-bar');
			if (tmpBar && tmpBar.parentNode) { tmpBar.parentNode.removeChild(tmpBar); }
		}
	}

	// Built from the same mark the card renders, so the tooltip can never drift from the row above it.
	_tooltipHTML(pCard)
	{
		let fEscape = (pValue) => String(pValue === null || typeof pValue === 'undefined' ? '' : pValue)
			.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
		let tmpCells = (pCard.Cells || []).map((pCell) =>
			'<div class="ps-cell"><span class="ps-cell-k">' + fEscape(pCell.K) + '</span>'
			+ '<span class="ps-cell-v">' + fEscape(pCell.V) + '</span></div>').join('');
		let tmpDescription = String(pCard.Description || '').slice(0, 400);
		return '<div class="ps-tip">'
			+ '<div class="ps-mark ' + fEscape(pCard.KindClass) + '">'
			+ '<span class="ps-mark-d">' + fEscape(pCard.Designator) + '</span>'
			+ '<span class="ps-mark-t">' + fEscape(pCard.Title) + '</span></div>'
			+ '<div class="ps-cells">' + tmpCells + '</div>'
			+ (tmpDescription ? ('<p class="ps-tip-desc">' + fEscape(tmpDescription) + '</p>') : '')
			+ '</div>';
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		this._applySelectionClasses();
		this._syncBulkBar();
		this._attachCardTooltips();
		this.setExpanded(this.getExpanded());
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}
}

module.exports = PictViewBoard;

module.exports.default_configuration =
{
	ViewIdentifier: 'Plansheet-Board',
	DefaultRenderable: 'Board-Display',
	AutoRender: false,
	CSS: /*css*/`
		.ps-board { height: 100%; display: flex; flex-direction: column; position: relative; }
		.ps-board-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
		.ps-board-head h2 { font-size: 22px; font-weight: 600; color: var(--theme-color-text-primary, #2c3140); margin: 0; }
		.ps-board-head-actions { display: flex; align-items: center; gap: 8px; }
		/* The board's single scroll region: one scrollbar pair for the whole card area (horizontal to
		   reach every lane, vertical for a tall board). align-items:flex-start lets each lane size to its
		   own content, so a busy lane grows here instead of scrolling inside itself. The lanes and their
		   card stacks never set overflow, so they can't spawn the nested column scrollbars that fight this one. */
		.ps-board-lanes { display: flex; gap: 12px; overflow: auto; align-items: flex-start; flex: 1; min-height: 0; padding-bottom: 8px; }
		.ps-lane {
			flex: 0 0 260px; width: 260px; background: var(--theme-color-background-secondary, #f1f3f7);
			border-radius: 10px; padding: 10px; display: flex; flex-direction: column;
		}
		.ps-lane-head {
			display: flex; align-items: center; justify-content: space-between;
			font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
			color: var(--theme-color-text-secondary, #5b6376); padding: 4px 6px 10px;
		}
		.ps-lane-name { display: inline-flex; align-items: center; }
		.ps-lane-dot { display: inline-block; width: 10px; height: 10px; border-radius: 3px; margin-right: 7px; flex: 0 0 auto; }
		.ps-lane-count {
			background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-secondary, #5b6376);
			border-radius: 999px; padding: 1px 8px; font-size: 12px;
		}
		.ps-lane-cards { display: flex; flex-direction: column; gap: 8px; min-height: 40px; }
		/* The overflow footer: what is not shown, and the way to show it. A ruled block rather than a
		   button spanning the lane, so it reads as a note at the foot of the column and never looks
		   like another card to drag. */
		.ps-lane-more {
			margin-top: 8px; width: 100%; padding: 6px 8px; display: flex; align-items: center; gap: 8px;
			background: var(--theme-color-background-panel, #fff);
			border: 1px dashed var(--theme-color-border-default, #d8dde6); border-radius: 8px;
		}
		.ps-lane-more-count {
			flex: 1; min-width: 0; font-size: 12px; font-variant-numeric: tabular-nums;
			color: var(--theme-color-text-muted, #8a93a6);
			overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
		}
		.ps-lane-more-btn {
			flex: 0 0 auto; font-size: 11.5px; font-weight: 600; padding: 3px 9px; cursor: pointer;
			color: var(--theme-color-text-secondary, #5b6376); background: transparent;
			border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 6px;
		}
		.ps-lane-more-btn:hover { border-color: var(--theme-color-brand-primary, #2988df); color: var(--theme-color-brand-primary, #2988df); }
		.ps-lane.ps-lane-dragover { background: var(--theme-color-background-hover, #e4e9f2); outline: 2px dashed var(--theme-color-brand-primary, #2988df); outline-offset: -2px; }
		.ps-card-dragging { opacity: 0.45; }
		.ps-card-placeholder {
			border: 2px dashed var(--theme-color-brand-primary, #2988df); border-radius: 8px;
			background: var(--theme-color-background-hover, rgba(41,136,223,0.10)); min-height: 42px;
			box-sizing: border-box; pointer-events: none;
		}
		.ps-card {
			background: var(--theme-color-background-panel, #fff); border-radius: 8px; padding: 10px 12px;
			border-left: 3px solid var(--theme-color-border-default, #d8dde6); cursor: grab;
			box-shadow: 0 1px 2px rgba(0,0,0,0.06);
		}
		.ps-card:active { cursor: grabbing; }
		.ps-card-feature { border-left-color: var(--theme-color-brand-primary, #2988df); }
		.ps-card-workitem { border-left-color: var(--theme-color-brand-secondary, #d99366); }
		.ps-card-row { display: flex; align-items: flex-start; gap: 8px; margin-bottom: 6px; }
		.ps-card-check { margin: 2px 0 0; flex: 0 0 auto; cursor: pointer; }
		.ps-card-feature .ps-card-check { display: none; } /* v1 bulk acts on WorkItems only */
		.ps-card-selected { outline: 2px solid var(--theme-color-brand-primary, #2988df); outline-offset: -1px; }
		.ps-card-title { font-size: 14px; color: var(--theme-color-text-primary, #2c3140); }
		.ps-card-row .ps-card-title { margin-bottom: 0; }
		.ps-card-meta { display: flex; gap: 6px; flex-wrap: wrap; }
		.ps-bulk-bar {
			position: absolute; left: 50%; transform: translateX(-50%); bottom: 18px; z-index: 40;
			display: flex; align-items: center; gap: 10px; max-width: calc(100% - 32px);
			background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #2c3140);
			border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 10px;
			padding: 8px 12px; box-shadow: 0 6px 24px rgba(0,0,0,0.18);
		}
		.ps-bulk-count { font-weight: 700; font-size: 13px; white-space: nowrap; }
		.ps-bulk-note { font-size: 12px; color: var(--theme-color-text-secondary, #5b6376); }
		.ps-bulk-spacer { flex: 1 1 auto; min-width: 4px; }
		.ps-bulk-select { font-size: 13px; padding: 4px 6px; border-radius: 6px; border: 1px solid var(--theme-color-border-default, #d8dde6); background: var(--theme-color-background-panel, #fff); color: inherit; }
		.ps-bulk-archive { color: var(--theme-color-status-danger, #c0392b); }
		.ps-bulk-confirm { font-weight: 700; }
		.ps-card-kind, .ps-card-pri {
			font-size: 11px; padding: 1px 7px; border-radius: 999px;
			background: var(--theme-color-background-secondary, #eef1f6); color: var(--theme-color-text-secondary, #5b6376);
		}
		.ps-card-pri:empty, .ps-card-kind:empty { display: none; }
		.ps-card-review { display: flex; gap: 6px; margin-top: 8px; }
		.ps-card-review-btn { flex: 1 1 auto; padding: 3px 6px; font-size: 11px; font-weight: 600; border-radius: 4px; border: 1px solid var(--theme-color-border-default,#d8dde6); background: var(--theme-color-surface-raised,#fff); color: var(--theme-color-text-secondary,#5b6376); cursor: pointer; }
		.ps-card-review-btn:hover { border-color: var(--theme-color-brand-primary,#2988df); }
		.ps-card-review-pass:hover { border-color: #3fae6b; color: #3fae6b; }
		.ps-card-review-fail:hover { border-color: #d0555b; color: #d0555b; }
		/* A card is the shared title block, relaxed: it keeps the designator cell and the rule, but the
		   cells underneath breathe rather than being hard drawing furniture at forty cards a lane. */
		.ps-card .ps-mark { border: 0; background: transparent; }
		/* The cell padding is title-block furniture: a ruled box needs breathing room inside its rules.
		   A card's mark has no rule and no fill, so that padding is vestigial -- and the 4px on top was
		   pushing the first line of text down until a 13px checkbox sat 4px above its optical centre,
		   which is what read as misaligned. Dropping it lands the checkbox within 0.3px of the text
		   centre with no magic number, and takes 4px off every card while it is at it. */
		.ps-card .ps-mark-d { border-right: 0; background: transparent; padding-top: 0; padding-left: 0;
			color: var(--theme-color-brand-primary, #3b6fd4); }
		/* One line, however long the title runs (WI #370): a title's length used to decide the card's
		   height, so a lane mixing a short title with a full-sentence one read as two different kinds
		   of card instead of one even stream. The full text is not lost -- it is still the first thing
		   in the hover tooltip (see _tooltipHTML) -- so clipping the card to a single line trades away
		   only the part that was making the board hard to scan. */
		.ps-card .ps-mark-t { padding-top: 0; padding-left: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
		.ps-card .ps-mark { align-items: center; }
		.ps-card .ps-mark-d { display: block; }
		/* The mark is the only clickable spot left on the card (see the Board-Card template comment) --
		   everything else is drag surface and the checkbox. cursor:pointer plus a dotted underline on
		   hover is the affordance that tells you which part of the card is a link, since the rest of it
		   no longer is. */
		.ps-card .ps-card-nav { cursor: pointer; }
		.ps-card .ps-card-nav:hover .ps-mark-t { text-decoration: underline dotted; text-underline-offset: 2px; }
		.ps-card .ps-cells { margin-top: 6px; }
		/* Four cells across a kanban lane is about 40px each, which elides the values worth reading
		   (Innovation, Enhancement, a person's name). A title block wraps its cells rather than
		   shrinking them, so these wrap two-up: same four facts, roughly double the width, one extra
		   line of height. Anything narrower was showing "Innovati..." and calling it information.
		   A grid, not a flex basis: the cells carry padding and the app has no global border-box, so a
		   percentage basis silently overflows and drops every cell onto its own row instead. */
		.ps-card .ps-cells { display: grid; grid-template-columns: 1fr 1fr; }
		.ps-card .ps-cell { flex: none; min-width: 0; }
		.ps-card .ps-cell:nth-child(2n) { border-right: 0; }
		.ps-card .ps-cell:nth-child(n+3) { border-top: 1px solid var(--theme-color-border-light, #dfd7c4); }
		/* By and For sit blank on most cards -- Type and Priority carry the reading, so those two rows
		   are hidden until "Expanded" is switched on. CSS-only (data-board-expanded on the root), so
		   flipping it never re-renders a single card. */
		#ps-board-root:not([data-board-expanded="1"]) .ps-card .ps-cell[data-k="By"],
		#ps-board-root:not([data-board-expanded="1"]) .ps-card .ps-cell[data-k="For"] { display: none; }
		.ps-board-expand-btn.is-active { background: var(--theme-color-brand-primary, #3b6fd4); color: #fff; }
	` + libMarkup.MarkupCSS,
	Templates:
	[
		// The mark, shared with every other surface. Spread first so a view-local template of the same
		// hash would win, which is the override seam if the board ever needs a one-off.
		...libMarkup.MarkupTemplates,
		{
			Hash: 'Board-Display',
			Template: /*html*/`
<div class="ps-page-bleed ps-board" id="ps-board-root" data-board-expanded="0">
	<div class="ps-board-head">
		<h2>Board</h2>
		<div class="ps-board-head-actions">
			<button class="ps-btn ps-btn-sm ps-board-expand-btn" id="ps-board-expand-btn" title="Show the By / For fields on every card" onclick="_Pict.views['Plansheet-Board'].toggleExpanded()">Expand</button>
			<button class="ps-btn" onclick="_Pict.views['Plansheet-Board'].refresh()">Refresh</button>
		</div>
	</div>
	{~TS:Board-FilterBar:AppData.Board.FilterBarSlot~}
	<div class="ps-board-lanes" id="ps-board-lanes">{~TS:Board-Lane:AppData.Board.Lanes~}</div>
</div>`
		},
		{
			Hash: 'Board-Lane',
			Template: /*html*/`
<div class="ps-lane" ondragover="_Pict.views['Plansheet-Board'].allowDrop(event)" ondragleave="_Pict.views['Plansheet-Board'].dragLeave(event)" ondrop="_Pict.views['Plansheet-Board'].drop(event,'{~D:Record.Key~}')">
	<div class="ps-lane-head"><span class="ps-lane-name"><span class="ps-lane-dot" style="background:{~D:Record.Color~}"></span>{~D:Record.Label~}</span><span class="ps-lane-count">{~D:Record.Count~}</span></div>
	<div class="ps-lane-cards">{~TS:Board-Card:Record.RenderCards~}</div>
	{~TS:Board-Lane-More:Record.MoreSlot~}
</div>`
		},
		{
			Hash: 'Board-Lane-More',
			Template: /*html*/`
<div class="ps-lane-more">
	<span class="ps-lane-more-count">{~D:Record.Label~}</span>
	<button class="ps-lane-more-btn" onclick="_Pict.views['Plansheet-Board'].laneMore('{~D:Record.Key~}','{~D:Record.Action~}')">{~D:Record.Button~}</button>
</div>`
		},
		{
			Hash: 'Board-Card',
			// Only the mark (the designator + title at the top) navigates -- the rest of the card is
			// for dragging and the checkbox, not a link. The whole card used to carry the
			// click-to-open handler, so missing the checkbox by even a pixel (its corners are
			// rounded, so the real hit target is smaller than it looks) bounced you to the detail
			// view instead of selecting the card, and getting back meant re-finding your place on
			// the board. ps-card-nav is the hover affordance (dotted underline) marking the one spot
			// that is actually a link.
			Template: /*html*/`
<div class="ps-card {~D:Record.KindClass~}" data-number="{~D:Record.Number~}" data-id="{~D:Record.ID~}" draggable="true" ondragstart="_Pict.views['Plansheet-Board'].dragStart(event,'{~D:Record.CardKind~}',{~D:Record.ID~})" ondragend="_Pict.views['Plansheet-Board'].dragEnd(event)">
	<div class="ps-card-row">
		<input type="checkbox" class="ps-card-check" title="Select" onclick="_Pict.views['Plansheet-Board'].toggleSelect(event,{~D:Record.Number~})">
		<div class="ps-mark ps-card-nav {~D:Record.KindClass~}" title="Open" onclick="_Pict.views['Plansheet-Board'].openCard('{~D:Record.CardKind~}',{~D:Record.ID~})"><span class="ps-mark-d">{~D:Record.Designator~}</span><span class="ps-mark-t">{~D:Record.Title~}</span></div>
	</div>
	<div class="ps-cells">{~TS:Markup-Cell:Record.Cells~}</div>
	{~TS:Board-Card-Review:Record.ReviewSlot~}
</div>`
		},
		{
			Hash: 'Board-Card-Review',
			Template: /*html*/`
<div class="ps-card-review">
	<button class="ps-card-review-btn ps-card-review-pass" title="Passed review -> Done" onclick="_Pict.views['Plansheet-Board'].reviewPass(event,{~D:Record.ID~})">Passed</button>
	<button class="ps-card-review-btn ps-card-review-fail" title="Did not pass QC -> send back for rework" onclick="_Pict.views['Plansheet-Board'].reviewFail(event,{~D:Record.ID~})">Send back</button>
</div>`
		},
		{
			Hash: 'Board-FilterBar',
			Template: /*html*/`
<div class="ps-filterbar">
	<div class="ps-filter-row">
		<input type="search" class="ps-filter-search" placeholder="Search cards..." value="{~D:Record.SearchValue~}" oninput="_Pict.views['Plansheet-Board'].setSearch(this.value)">
		<select class="ps-filter-select ps-filter-views" onchange="_Pict.views['Plansheet-Board'].applyView(this.value)">{~TS:Board-Filter-Option:Record.ViewOptions~}</select>
		<select class="ps-filter-select" onchange="_Pict.views['Plansheet-Board'].setFilter('type', this.value)">{~TS:Board-Filter-Option:Record.TypeOptions~}</select>
		<select class="ps-filter-select" onchange="_Pict.views['Plansheet-Board'].setFilter('priority', this.value)">{~TS:Board-Filter-Option:Record.PriorityOptions~}</select>
		<select class="ps-filter-select" onchange="_Pict.views['Plansheet-Board'].setFilter('assignee', this.value)">{~TS:Board-Filter-Option:Record.AssigneeOptions~}</select>
		<select class="ps-filter-select" onchange="_Pict.views['Plansheet-Board'].setFilter('recent', this.value)">{~TS:Board-Filter-Option:Record.RecentOptions~}</select>
		<select class="ps-filter-select" onchange="_Pict.views['Plansheet-Board'].setFilter('wfstatus', this.value)">{~TS:Board-Filter-Option:Record.WorkflowStatusOptions~}</select>
		<button class="ps-btn ps-btn-sm" onclick="_Pict.views['Plansheet-Board'].saveView()">Save view</button>
		{~TS:Board-Filter-DeleteView:Record.DeleteViewSlot~}
		{~TS:Board-Filter-Clear:Record.ClearSlot~}
		<span class="ps-filter-spacer"></span>
		<span class="ps-filter-count" id="ps-board-count">{~D:Record.CountLabel~}</span>
	</div>
</div>`
		},
		{ Hash: 'Board-Filter-Option', Template: /*html*/`<option value="{~D:Record.Value~}" {~D:Record.SelectedAttr~}>{~D:Record.Label~}</option>` },
		{ Hash: 'Board-Filter-Clear', Template: /*html*/`<button class="ps-btn ps-btn-sm" onclick="_Pict.views['Plansheet-Board'].clearFilters()">Clear</button>` },
		{ Hash: 'Board-Filter-DeleteView', Template: /*html*/`<button class="ps-btn ps-btn-sm ps-btn-danger" onclick="_Pict.views['Plansheet-Board'].deleteView('{~D:Record.Key~}')">Delete view</button>` }
	],
	Renderables:
	[
		{
			RenderableHash: 'Board-Display',
			TemplateHash: 'Board-Display',
			ContentDestinationAddress: '#Plansheet-Workspace'
		}
	]
};
